import { FC, PropsWithChildren } from "react";

import { Analytics, Card as CardType } from "@/types/General";
import { Grid, Masonry, Stack } from "../Layouts";
import { InfiniteScroll } from "../InfiniteScroll";
import { Button } from "../Button";
import { Card } from "../Card";
import { getDataAutomationId, getDataLinkAttributes } from "@/helpers";

type MasonryLayout = {
  layoutType: "masonry";
  numberOfColumns: 2;
};

type GridLayout = {
  layoutType: "grid";
  numberOfColumns: 2 | 3 | 4;
};
type StackLayout = {
  layoutType: "stack";
};

type LayoutType = MasonryLayout | GridLayout | StackLayout;

type InfiniteScroll = {
  paginationType: "infinite-scroll";
  isLoading: boolean;
  isEndOfPage: boolean;
  loadingComponent: JSX.Element;
  onGoToNextPage: () => void;
};

type ExpandableSection = {
  paginationType: "expandable-section";
  viewMoreText: { text: string } & Analytics;
  viewLessText: { text: string } & Analytics;
  isEndOfPage: boolean;
  numberOfResults: number;
  isLoading: boolean;
  loadingComponent: JSX.Element;
  onGoToFirstPage: () => void;
  onGoToLastPage: () => void;
};

type PaginationType = InfiniteScroll | ExpandableSection;

type Section = {
  text: string;
  predicate?: (card: CardType) => boolean;
  orderCardsByTitle?: string[];
} & LayoutType;

type BaseProps = {
  displayedCards: CardType[];
  hiddenCards: CardType[];
  sections: Section[];
};

type IndexCardsProps = BaseProps & PaginationType;

type SectionProps = Section & {
  displayedCards: CardType[];
  hiddenCards: CardType[];
  showCount: boolean;
};

const HiddenCards: FC<{ cards: CardType[] }> = ({ cards }) => (
  <div className="hidden" aria-hidden>
    {cards.map((card, index) => (
      <Card key={`${card?.title}-${card?.description}-${index}`} {...card} />
    ))}
  </div>
);

const ProductSection: FC<SectionProps> = (section) => {
  const productCount =
    section.displayedCards.length + section.hiddenCards.length;

  return (
    <section key={section.text} className="flex flex-col">
      {section.text && (
        <div className="border-headline mb-4 flex items-end justify-between gap-4 border-b-2 pb-3">
          <h2 className="text-headline text-subhead-xs sm:text-subhead-xl font-bold">
            {section.text}
          </h2>
          {section.showCount && (
            <span className="text-body-secondary text-body-s shrink-0">
              {productCount} {productCount === 1 ? "product" : "products"}
            </span>
          )}
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2">
        {section.displayedCards.map((card, index) => (
          <div
            className={`border-swdc-gray-100 sm:odd:border-r ${
              index < section.displayedCards.length - 1 ? "border-b" : ""
            } ${
              index >=
              section.displayedCards.length -
                (section.displayedCards.length % 2 || 2)
                ? "sm:border-b-0"
                : ""
            }`}
            key={`${card.title}-${card.description}-${index}`}
          >
            <Card {...card} />
          </div>
        ))}
      </div>
      <HiddenCards cards={section.hiddenCards} />
    </section>
  );
};

const DefaultSection: FC<SectionProps> = (section) => (
  <section key={section.text} className="flex flex-col gap-y-8 lg:gap-y-3">
    {section.text && (
      <span className="text-body-secondary text-subhead-2xs font-bold uppercase">
        {section.text}
      </span>
    )}
    <CardsLayout {...section}>
      {section.displayedCards.map((card, index) =>
        section.layoutType === "masonry" ? (
          <div
            className="mb-4"
            key={`${card?.title}-${card?.description}-${index}`}
          >
            <Card {...card} />
          </div>
        ) : (
          <Card
            key={`${card?.title}-${card?.description}-${index}`}
            {...card}
          />
        ),
      )}
    </CardsLayout>
    <HiddenCards cards={section.hiddenCards} />
  </section>
);

const Section: FC<SectionProps> = (section) =>
  section.displayedCards.length > 0 &&
  section.displayedCards.every((card) => card.type === "Product") ? (
    <ProductSection {...section} />
  ) : (
    <DefaultSection {...section} />
  );

const LayoutWithInfiniteScroll: FC<InfiniteScroll & BaseProps> = (props) => (
  <div>
    <div
      className={`flex flex-col ${
        props.displayedCards.length > 0 &&
        props.displayedCards.every((card) => card.type === "Product")
          ? "gap-y-14"
          : "gap-y-7.5"
      }`}
    >
      {props.sections.map((section) => {
        const cards = section.predicate
          ? props.displayedCards.reduce((acc, current) => {
              if (current.type === "Download" || current.type === "Product") {
                const { modal, ...card } = current;
                if (section.predicate?.(card as CardType)) {
                  return [...acc, current];
                }
                return acc;
              }

              if (section.predicate?.(current)) {
                return [...acc, current];
              }
              return acc;
            }, [] as CardType[])
          : props.displayedCards;

        const hiddenCards = section.predicate
          ? props.hiddenCards.reduce((acc, current) => {
              if (current.type === "Download" || current.type === "Product") {
                const { modal, ...card } = current;
                if (section.predicate?.(card as CardType)) {
                  return [...acc, current];
                }
                return acc;
              }

              if (section.predicate?.(current)) {
                return [...acc, current];
              }
              return acc;
            }, [] as CardType[])
          : props.hiddenCards;

        const {
          displayedCards: orderedCards,
          hiddenCards: orderedHiddenCards,
        } =
          hasProductCard(cards) &&
          section.orderCardsByTitle &&
          !!section.orderCardsByTitle.length
            ? orderByTitleAndSplitCards(
                cards,
                hiddenCards,
                section.orderCardsByTitle,
              )
            : { displayedCards: cards, hiddenCards };
        return cards.length > 0 ? (
          <Section
            key={section.text ?? section.layoutType + JSON.stringify(cards)}
            displayedCards={orderedCards}
            hiddenCards={orderedHiddenCards}
            showCount={props.isEndOfPage || props.hiddenCards.length > 0}
            {...section}
          />
        ) : null;
      })}
    </div>
    {props.isLoading && props.loadingComponent}
    {!props.isEndOfPage && !props.isLoading && (
      <InfiniteScroll onGoToNextPage={props.onGoToNextPage} />
    )}
  </div>
);

const ExpandableSection: FC<
  ExpandableSection &
    Section & {
      displayedCards: CardType[];
      hiddenCards: CardType[];
    }
> = (props) => {
  const filteredCards = props.predicate
    ? props.displayedCards.filter(props.predicate)
    : props.displayedCards;
  const hiddenFilteredCards = props.predicate
    ? props.hiddenCards.filter(props.predicate)
    : props.hiddenCards;
  const {
    displayedCards: orderedFilteredCards,
    hiddenCards: orderedHiddenFilteredCards,
  } =
    hasProductCard(filteredCards) &&
    props.orderCardsByTitle &&
    !!props.orderCardsByTitle.length
      ? orderByTitleAndSplitCards(
          filteredCards,
          hiddenFilteredCards,
          props.orderCardsByTitle,
        )
      : { displayedCards: filteredCards, hiddenCards: hiddenFilteredCards };
  const isViewLess = orderedFilteredCards.length >= props.numberOfResults;

  const buttonText = isViewLess ? props.viewLessText : props.viewMoreText;

  return orderedFilteredCards.length > 0 ? (
    <div className="flex flex-col gap-y-12">
      <Section
        {...props}
        displayedCards={orderedFilteredCards}
        hiddenCards={orderedHiddenFilteredCards}
        showCount={props.isEndOfPage || props.hiddenCards.length > 0}
      />
      {!props.isEndOfPage && (
        <Button
          variant="tertiary"
          icon={isViewLess ? "arrowUp" : "arrowDown"}
          className="mx-auto capitalize"
          onClick={() => {
            if (isViewLess) {
              props.onGoToFirstPage();
            } else {
              props.onGoToLastPage();
            }
          }}
          {...getDataLinkAttributes({
            dld: buttonText.dld,
            dlt: buttonText.dlt,
          })}
          {...getDataAutomationId(
            isViewLess ? "view-less-button" : "view-more-button",
          )}
        >
          {buttonText.text}
        </Button>
      )}
    </div>
  ) : (
    props.isLoading && props.loadingComponent
  );
};
const LayoutWithExpandableSection: FC<ExpandableSection & BaseProps> = (
  props,
) => {
  return (
    <div className="flex flex-col gap-y-14">
      {props.sections.map((section) => (
        <ExpandableSection key={section.text} {...section} {...props} />
      ))}
    </div>
  );
};

const CardsLayout: FC<PropsWithChildren<LayoutType>> = (props) => {
  switch (props.layoutType) {
    case "grid": {
      return <Grid {...props} />;
    }
    case "masonry": {
      return <Masonry {...props} />;
    }
    case "stack": {
      return <Stack {...props} />;
    }
  }
};

const hasProductCard = (cards: CardType[]) =>
  cards.some((card) => card.type === "Product");

const normalizeTitle = (title: string) => title.trim().toLowerCase();

const orderCardsByTitle = (cards: CardType[], titlesOrder: string[] = []) => {
  const priorities = new Map<string, number>();
  titlesOrder.forEach((title, index) => {
    const key = normalizeTitle(title);
    if (key && !priorities.has(key)) {
      priorities.set(key, index);
    }
  });

  const rank = (card: CardType) =>
    priorities.get(normalizeTitle(card.title)) ?? titlesOrder.length;

  const sortedProducts = cards
    .filter((card) => card.type === "Product")
    .sort((a, b) => rank(a) - rank(b));

  let next = 0;
  return cards.map((card) =>
    card.type === "Product" ? sortedProducts[next++] : card,
  );
};

const orderByTitleAndSplitCards = (
  displayedCards: CardType[],
  hiddenCards: CardType[],
  titlesOrder: string[],
) => {
  const orderedCards = orderCardsByTitle(
    [...displayedCards, ...hiddenCards],
    titlesOrder,
  );
  return {
    displayedCards: orderedCards.slice(0, displayedCards.length),
    hiddenCards: orderedCards.slice(displayedCards.length),
  };
};

export const IndexCards: FC<IndexCardsProps> = (props) => {
  switch (props.paginationType) {
    case "infinite-scroll": {
      return <LayoutWithInfiniteScroll {...props} />;
    }
    case "expandable-section": {
      return <LayoutWithExpandableSection {...props} />;
    }
  }
};
